import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminScenarioUploader from './AdminScenarioUploader';

const AdminHomePage = () => {
    const navigate = useNavigate();
    const [isAdmin, setIsAdmin] = useState(false);
    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(1);
    const [totalUsers, setTotalUsers] = useState(0);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [combinedUsers, setCombinedUsers] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [updatedNames, setUpdatedNames] = useState({});
    const [userStats, setUserStats] = useState([]);
    const [statsTotal, setStatsTotal] = useState(0);
    const [statsPage, setStatsPage] = useState(1);

    //pull user stats
    useEffect(() => {
        const fetchStats = async () => {
          try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/user-stats?page=${statsPage}&limit=25`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await res.json();
            setUserStats(data.users);
            setStatsTotal(data.total);
          } catch (err) {
            console.error("Error fetching user stats:", err);
          }
        };
      
        if (isAdmin) fetchStats();
      }, [isAdmin, statsPage]);

    //check for admin
    useEffect(() => {
        const checkAdmin = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/check-admin`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                setIsAdmin(response.data.isAdmin);
            } catch (error) {
                console.error('Access Denied:', error);
                navigate('/HomePage'); 
            }
        };

        checkAdmin();
    }, [navigate]);

    useEffect(() => {
        if (isAdmin) fetchUsers();
    }, [isAdmin, page, search]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/users?page=${page}&limit=25&search=${search}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });

            setUsers(response.data.users);
            setTotalUsers(response.data.total);
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateUser = async (userId, updatedData) => {
        try {
            await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/admin/update-user`, {
                userId,
                updates: updatedData
            }, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });

            setUsers(users.map(user => user.id === userId ? { ...user, ...updatedData } : user));
        } catch (error) {
            console.error('Error updating user:', error);
            alert('Failed to update user');
        }
    };

    // Export users from Firebase to list
    const exportUsers = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/export-users`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
    
            if (!response.ok) {
                throw new Error('Failed to export users');
            }
    
            const data = await response.json();
            const blob = new Blob([JSON.stringify(data.users, null, 2)], { type: 'application/json' });
    
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = 'users_export.json';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
    
            alert('User list exported successfully!');
        } catch (error) {
            console.error('🚨 Error exporting users:', error);
            alert('Failed to export user list');
        }
    };
      
    useEffect(() => {
        const fetchAuthAndDbUsers = async () => {
          try {
            const [authRes, dbRes] = await Promise.all([
              fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/export-users`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
              }),
              fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/users?page=1&limit=1000`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
              }),
            ]);
      
            const authData = await authRes.json();
            const dbData = await dbRes.json();
      
            console.log('✅ Full Auth Response:', authData);
            console.log('✅ Full DB Response:', dbData);
      
            // Use fallback if authData.users is undefined
            const merged = (authData.users || []).map(authUser => {
              const match = dbData.users.find(dbUser => dbUser.id === authUser.uid);
              return {
                uid: authUser.uid,
                email: authUser.email,
                displayName: authUser.displayName,
                phoneNumber: authUser.phoneNumber || '',
                disabled: authUser.disabled,
                creationTime: authUser.creationTime,
                lastSignInTime: authUser.lastSignInTime,
                dbFirstName: match?.firstName || '',
                dbLastName: match?.lastName || ''
              };
            });
      
            setCombinedUsers(merged);
          } catch (error) {
            console.error("❌ Failed to combine auth/db users:", error);
          }
        };
      
        if (isAdmin) {
          fetchAuthAndDbUsers();
        }
      }, [isAdmin]);

    const handleInputChange = (uid, value) => {
        setUpdatedNames(prev => ({ ...prev, [uid]: value }));
    };

    const updateDisplayName = async (uid) => {
        const newDisplayName = updatedNames[uid];
        if (!newDisplayName) return;

        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/update-display-name`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify({ uid, newDisplayName }),
              });

            if (!res.ok) throw new Error('Failed to update');

            alert('Display name updated!');
            // Optionally refetch users to refresh the table
        } catch (err) {
            console.error(err);
            alert('Error updating display name');
        }
    };

    // Sorting Logic for Firebase Auth Table
    const sortedUsers = useMemo(() => {
        if (!sortConfig.key) return combinedUsers;
      
        const sorted = [...combinedUsers].sort((a, b) => {
          const valA = a[sortConfig.key];
          const valB = b[sortConfig.key];
      
          // For timestamps
          if (sortConfig.key.includes('Time') && valA && valB) {
            return new Date(valA) - new Date(valB);
          }
      
          if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
          if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        });
      
        return sorted;
      }, [combinedUsers, sortConfig]);
      
      const handleSort = (key) => {
        setSortConfig((prev) => {
          if (prev.key === key) {
            return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
          }
          return { key, direction: 'asc' };
        });
      };


      if (!isAdmin) {
        return <p>Loading...</p>;
    }

    return (
        <div className="max-w-10xl mx-auto px-6 py-10">
            <div className="flex items-center gap-8 mb-6">
                <h1 className="text-3xl font-bold text-gray-800">Admin Dashboard</h1>
                <a
                    href="/admin-ui"
                    className="text-sm text-blue-600 hover:underline bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-md border border-blue-200"
                >
                    Bulk User Update Page
                </a>
            </div>

            <div className="mt-10">
                <h2 className="text-xl font-semibold mb-2">User Stats</h2>
                <table className="min-w-full divide-y divide-gray-200 bg-white rounded shadow text-sm">
                    <thead className="bg-gray-100 text-gray-700 font-semibold">
                        <tr>
                            <th className="px-4 py-2 text-left">Name</th>
                            <th className="px-4 py-2 text-left">Email</th>
                            <th className="px-4 py-2 text-center">Conversations</th>
                            <th className="px-4 py-2 text-center">Reports</th>
                        </tr>
                    </thead>
                    <tbody className="text-gray-800 divide-y divide-gray-100">
                        {Array.isArray(userStats) ? (
                        userStats.map((user) => (
                            <tr key={user.id}>
                            <td className="px-4 py-2">{user.name}</td>
                            <td className="px-4 py-2">{user.email}</td>
                            <td className="px-4 py-2 text-center">{user.conversationCount}</td>
                            <td className="px-4 py-2 text-center">{user.reportCount}</td>
                            </tr>
                        ))
                        ) : (
                        <tr>
                            <td colSpan="4" className="px-4 py-2 text-center text-gray-500">No data available</td>
                        </tr>
                        )}
                    </tbody>
                    </table>

                <div className="mt-4 flex justify-center items-center gap-4">
                    <button
                        onClick={() => setStatsPage(statsPage - 1)}
                        disabled={statsPage === 1}
                        className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                    >
                        Previous
                    </button>
                    <span className="text-gray-700">Page {statsPage}</span>
                    <button
                        onClick={() => setStatsPage(statsPage + 1)}
                        disabled={statsPage * 25 >= statsTotal}
                        className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
            </div>


          <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <button
              onClick={exportUsers}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded"
            >
              Export Users
            </button>
      
            <input
              type="text"
              placeholder="Search by email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border border-gray-300 rounded px-4 py-2 w-full md:w-1/3"
            />
          </div>
      
          {loading ? (
            <p className="text-center text-gray-600">Loading users...</p>
          ) : (
            <div className="overflow-x-auto bg-white rounded shadow">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-100 text-gray-700 font-semibold">
                  <tr>
                    <th className="px-4 py-2 text-left">User ID</th>
                    <th className="px-4 py-2">Email</th>
                    <th className="px-4 py-2">First Name</th>
                    <th className="px-4 py-2">Last Name</th>
                    <th className="px-4 py-2">Job Title</th>
                    <th className="px-4 py-2">Organization</th>
                    <th className="px-4 py-2">Plan</th>
                    <th className="px-4 py-2">Active</th>
                    <th className="px-4 py-2">Disposition</th>
                    <th className="px-4 py-2">Terms</th>
                    <th className="px-4 py-2">Created</th>
                    <th className="px-4 py-2">Traits</th>
                  </tr>
                </thead>
                <tbody className="text-gray-800 divide-y divide-gray-100">
                  {users.map((user) => (
                    <tr key={user.id}>
                        <td className="px-4 py-2">{user.id}</td>
                        <td className="px-4 py-2 min-w-[250px]">
                            <input
                            type="email"
                            defaultValue={user.email}
                            onBlur={(e) => updateUser(user.id, { email: e.target.value })}
                            className="border rounded px-2 py-1 w-full"
                            />
                        </td>
                        <td className="px-4 py-2 min-w-[150px]">
                            <input
                            type="text"
                            defaultValue={user.firstName}
                            onBlur={(e) => updateUser(user.id, { firstName: e.target.value })}
                            className="border rounded px-2 py-1 w-full"
                            />
                        </td>
                        <td className="px-4 py-2 min-w-[150px]">
                            <input
                            type="text"
                            defaultValue={user.lastName}
                            onBlur={(e) => updateUser(user.id, { lastName: e.target.value })}
                            className="border rounded px-2 py-1 w-full"
                            />
                        </td>
                        <td className="px-4 py-2">
                            <input
                            type="text"
                            defaultValue={user.jobTitle}
                            onBlur={(e) => updateUser(user.id, { jobTitle: e.target.value })}
                            className="border rounded px-2 py-1 w-full"
                            />
                        </td>
                        <td className="px-4 py-2">
                            <input
                            type="text"
                            defaultValue={user.organization}
                            onBlur={(e) => updateUser(user.id, { organization: e.target.value })}
                            className="border rounded px-2 py-1 w-full"
                            />
                        </td>
                        <td className="px-4 py-2">
                            <select
                            defaultValue={user.planType}
                            onChange={(e) => updateUser(user.id, { planType: e.target.value })}
                            className="border rounded px-2 py-1"
                            >
                            <option value="monthly">Monthly</option>
                            <option value="annual">Annual</option>
                            </select>
                        </td>
                        <td className="px-4 py-2 text-center">
                            <input
                            type="checkbox"
                            checked={user.activeStatus}
                            onChange={(e) => updateUser(user.id, { activeStatus: e.target.checked })}
                            />
                        </td>
                        <td className="px-4 py-2 text-center">
                            <input
                            type="checkbox"
                            checked={user.isDispositionEnabled}
                            onChange={(e) => updateUser(user.id, { isDispositionEnabled: e.target.checked })}
                            />
                        </td>
                        <td className="px-4 py-2 text-center">
                            <input
                            type="checkbox"
                            checked={user.termsAccepted}
                            onChange={(e) => updateUser(user.id, { termsAccepted: e.target.checked })}
                            />
                        </td>
                        <td className="px-4 py-2 text-xs">
                            {new Date(user.createdAt?._seconds * 1000).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-2 w-64 align-top">
                            <div className="flex gap-2 text-xs overflow-x-auto whitespace-nowrap max-w-full scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
                                {Object.entries(user.dispositionalTraits || {}).map(([key, value]) => (
                                <div key={key} className="flex items-center gap-1 border border-gray-200 px-2 py-1 rounded">
                                    <span className="whitespace-nowrap">{key}:</span>
                                    <input
                                    type="number"
                                    min="1"
                                    max="5"
                                    defaultValue={value}
                                    onBlur={(e) =>
                                        updateUser(user.id, {
                                        [`dispositionalTraits.${key}`]: Number(e.target.value),
                                        })
                                    }
                                    className="border rounded px-1 py-0.5 w-10 text-center"
                                    />
                                </div>
                                ))}
                            </div>
                        </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      
          <div className="mt-6 flex justify-center items-center gap-4">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-gray-700">Page {page}</span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page * 25 >= totalUsers}
              className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              Next
            </button>
          </div>

            <div className="mt-10 bg-white p-6 rounded shadow-md">
                <h2 className="text-xl font-bold mb-4">Display Name Comparison</h2>
                <table className="w-full table-auto border border-gray-200 text-sm">
                    <thead>
                    <tr className="bg-gray-100 text-left">
                        <th
                            onClick={() => handleSort('uid')}
                            className="px-4 py-2 border-b cursor-pointer hover:bg-gray-200"
                        >
                            UID {sortConfig.key === 'uid' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>

                        <th
                            onClick={() => handleSort('email')}
                            className="px-4 py-2 border-b cursor-pointer hover:bg-gray-200"
                        >
                            Email {sortConfig.key === 'email' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>

                        <th
                            onClick={() => handleSort('displayName')}
                            className="px-4 py-2 border-b cursor-pointer hover:bg-gray-200"
                        >
                            Auth Display Name {sortConfig.key === 'displayName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>

                        <th
                            onClick={() => handleSort('dbFirstName')}
                            className="px-4 py-2 border-b cursor-pointer hover:bg-gray-200"
                        >
                            DB First Name {sortConfig.key === 'dbFirstName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>

                        <th
                            onClick={() => handleSort('dbLastName')}
                            className="px-4 py-2 border-b cursor-pointer hover:bg-gray-200"
                        >
                            DB Last Name {sortConfig.key === 'dbLastName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>

                        <th
                            onClick={() => handleSort('phoneNumber')}
                            className="px-4 py-2 border-b cursor-pointer hover:bg-gray-200"
                        >
                            Phone {sortConfig.key === 'phoneNumber' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>

                        <th
                            onClick={() => handleSort('disabled')}
                            className="px-4 py-2 border-b cursor-pointer hover:bg-gray-200"
                        >
                            Status {sortConfig.key === 'disabled' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>

                        <th
                            onClick={() => handleSort('creationTime')}
                            className="px-4 py-2 border-b cursor-pointer hover:bg-gray-200"
                        >
                            Created {sortConfig.key === 'creationTime' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>

                        <th
                            onClick={() => handleSort('lastSignInTime')}
                            className="px-4 py-2 border-b cursor-pointer hover:bg-gray-200"
                        >
                            Last Sign-in {sortConfig.key === 'lastSignInTime' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    {sortedUsers.map((user, idx) => (
                        <tr key={idx} className="border-b">
                            <td className="px-4 py-2">{user.uid}</td>
                            <td className="px-4 py-2">{user.email}</td>
                            <td className="px-4 py-2">
                                <input
                                    type="text"
                                    value={(updatedNames[user.uid] ?? user.displayName) || ''}
                                    onChange={(e) => handleInputChange(user.uid, e.target.value)}
                                    className="border px-2 py-1 text-sm w-full rounded"
                                />
                                <button
                                    onClick={() => updateDisplayName(user.uid)}
                                    className="mt-1 text-blue-600 hover:underline text-xs"
                                >
                                    Save
                                </button>
                            </td>
                            <td className="px-4 py-2">{user.dbFirstName || <span className="text-gray-400 italic">None</span>}</td>
                            <td className="px-4 py-2">{user.dbLastName || <span className="text-gray-400 italic">None</span>}</td>
                            <td className="px-4 py-2">{user.phoneNumber || '-'}</td>
                            <td className="px-4 py-2">{user.disabled ? 'Disabled' : 'Active'}</td>
                            <td className="px-4 py-2 text-xs text-gray-600">
                                {user.creationTime ? new Date(user.creationTime).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-4 py-2 text-xs text-gray-600">
                                {user.lastSignInTime ? new Date(user.lastSignInTime).toLocaleDateString() : '-'}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            <div className="mt-10 border-t pt-8">
                <h2 className="text-2xl font-semibold mb-4">Upload a New Scenario</h2>
                <AdminScenarioUploader />
            </div>
        </div>
      );
      
};

export default AdminHomePage;
